import React from 'react'

export type GlossaryReadinessViewModel = {
  state: 'no_target' | 'glossary_missing' | 'glossary_running' | 'glossary_needs_review' | 'glossary_ready'
  title: string
  message: React.ReactNode
  tone: 'neutral' | 'info' | 'warning' | 'success'
  isBlocking?: boolean
}

export type TranslationQualitySummaryItem = {
  key: string
  label: string
  value: React.ReactNode
  tone?: 'neutral' | 'info'
  helpText?: React.ReactNode
}

export const resolveEasyModeQualitySummary = ({
  hasTargetSelection,
  isProofreadIncluded,
}: {
  hasTargetSelection: boolean
  isProofreadIncluded: boolean
}) => {
  if (!hasTargetSelection) return 'Select a target language to view quality settings'
  return isProofreadIncluded ? 'Optimized for this language' : 'Standard translation quality'
}

export const resolveAdvancedModelDisplay = ({
  modelSummary,
}: {
  modelSummary: React.ReactNode
}) => modelSummary

export const resolveTranslationSummaryState = ({
  hasTargetSelection,
  isMultiTargetMode,
  versionLabel,
  targetLanguage,
  targetLanguageCount,
}: {
  hasTargetSelection: boolean
  isMultiTargetMode: boolean
  versionLabel: string
  targetLanguage: string
  targetLanguageCount: number
}) => {
  if (!hasTargetSelection) {
    return 'Choose a target language to continue.'
  }
  if (isMultiTargetMode) {
    return `We'll translate your ${versionLabel || 'Original'} manuscript into ${targetLanguageCount} target languages.`
  }
  return `We'll translate your ${versionLabel || 'Original'} manuscript into ${targetLanguage}.`
}

export const resolveQualitySummary = ({
  hasTargetSelection,
  modelLabel = 'AI model',
  modelSummary,
  quotationSummary,
  proofreadSummary,
  characterAnalysisSummary,
  frenchSummary,
}: {
  hasTargetSelection: boolean
  modelLabel?: string
  modelSummary: React.ReactNode
  quotationSummary?: React.ReactNode
  proofreadSummary: React.ReactNode
  characterAnalysisSummary: React.ReactNode
  frenchSummary?: React.ReactNode
}): TranslationQualitySummaryItem[] => {
  const items: TranslationQualitySummaryItem[] = [
    {
      key: 'model',
      label: modelLabel,
      value: hasTargetSelection ? modelSummary : 'Select a target language to auto-pick a model',
      helpText: modelLabel === 'Recommended setup'
        ? 'ScribeShadow chooses the recommended translation model automatically in quick setup.'
        : undefined,
    },
    {
      key: 'quotation',
      label: 'Quotation style',
      value: hasTargetSelection ? (quotationSummary || 'Automatic') : 'Will be chosen after target selection',
      helpText: 'Controls how dialogue punctuation appears in the translated text.',
    },
    {
      key: 'quality-pass',
      label: 'Quality pass',
      value: hasTargetSelection ? proofreadSummary : 'Will follow the selected model and workflow',
    },
    {
      key: 'character-analysis',
      label: 'Character analysis',
      value: hasTargetSelection ? characterAnalysisSummary : 'Will follow the selected workflow',
      helpText: 'Helps keep pronouns, formality, and character references consistent.',
    },
  ]
  if (hasTargetSelection && frenchSummary) {
    items.push({
      key: 'french',
      label: 'French narration',
      value: frenchSummary,
      tone: 'info',
    })
  }
  return items
}

export const resolveFrenchLanguageBehaviorSummary = ({
  isFrenchTarget,
  toneSummary,
  dialogueSummary,
  rulesetSummary,
}: {
  isFrenchTarget: boolean
  toneSummary?: React.ReactNode
  dialogueSummary?: React.ReactNode
  rulesetSummary?: React.ReactNode
}): React.ReactNode => {
  if (!isFrenchTarget) return undefined
  if (toneSummary) {
    return 'Narrative voice will be preserved.'
  }
  return undefined
}

export const buildTranslationReadinessViewModel = ({
  hasTargetSelection,
  state,
  warningMessage,
  needsReviewMessage,
}: {
  hasTargetSelection: boolean
  state?: 'missing' | 'running' | 'failed' | 'needs_review' | null
  warningMessage?: React.ReactNode
  needsReviewMessage?: React.ReactNode
}): GlossaryReadinessViewModel => {
  if (!hasTargetSelection) {
    return {
      state: 'no_target',
      title: 'Target language needed',
      message: 'Please select a target language to continue.',
      tone: 'neutral',
      isBlocking: true,
    }
  }
  if (state === 'needs_review') {
    return {
      state: 'glossary_needs_review',
      title: 'Glossary needs review',
      message: needsReviewMessage || 'AI Glossary Scan has items pending review.',
      tone: 'warning',
    }
  }
  if (state === 'running') {
    return {
      state: 'glossary_running',
      title: 'Glossary scan running',
      message: warningMessage || 'AI Glossary Scan is running. Please wait for it to finish before translating.',
      tone: 'warning',
    }
  }
  if (state === 'missing' || state === 'failed') {
    return {
      state: 'glossary_missing',
      title: state === 'failed' ? 'Glossary scan needs attention' : 'Glossary scan not run',
      message: warningMessage || 'AI Glossary Scan needs attention before translation.',
      tone: 'warning',
    }
  }
  return {
    state: 'glossary_ready',
    title: 'Glossary ready',
    message: 'AI Glossary Scan is ready for this translation.',
    tone: 'neutral',
  }
}

export const buildGlossaryReadinessViewModel = buildTranslationReadinessViewModel
